import React from "react";
import { Box } from "@mui/material";
import { Button } from "components/haven-design/buttons";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import { BulkOwnerFailure, BulkOwnerResult } from "./bulk-owner-feedback";
import { countOf } from "./utils";

export type BulkResultSummaryProps = {
  result: BulkOwnerResult;
  onRetry: () => void;
  onClose: () => void;
  isRetrying: boolean;

  /** Shown under the failures when the retry itself fails outright. */
  error?: React.ReactNode;
};

const failureName = (failure: BulkOwnerFailure): string => failure.name ?? failure.interestId;

const failureLabel = (failure: BulkOwnerFailure): string =>
  (failure.reason ? `${failureName(failure)} — ${failure.reason}` : failureName(failure));

const failedHeading = (count: number): string =>
  `${countOf(count, "application")} could not be updated:`;

const BulkResultSummary: React.FC<BulkResultSummaryProps> = ({
  result,
  onRetry,
  onClose,
  isRetrying,
  error,
}) => {
  const total = result.succeeded.length + result.failed.length;

  return (
    <>
      <DialogContent>
        <p>{`${result.succeeded.length} of ${total} succeeded`}</p>
        <Box component="p" sx={{ color: "error.main" }}>{failedHeading(result.failed.length)}</Box>
        <Box component="ul" sx={{ color: "error.main", m: 0, pl: "20px" }}>
          {result.failed.map((failure) => (
            <li key={failure.interestId}>{failureLabel(failure)}</li>
          ))}
        </Box>
        {error}
      </DialogContent>
      <DialogActions>
        <Button
          color="secondary"
          size="S"
          onClick={onClose}
          disabled={isRetrying}
        >
          Close
        </Button>
        <Button
          color="primary"
          size="S"
          onClick={onRetry}
          disabled={isRetrying}
        >
          {isRetrying ? "Retrying" : "Retry failed"}
        </Button>
      </DialogActions>
    </>
  );
};

export default BulkResultSummary;
